import '@fontsource/noto-sans/300.css'
import '@fontsource/noto-sans/400.css'
import '@fontsource/noto-sans/500.css'
import '@fontsource/noto-sans/600.css'
import '@fontsource/noto-sans/700.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import React from 'react'

// Extend MUI Autocomplete types to support the custom `checkboxes` prop used in defaultProps below.
declare module '@mui/material/Autocomplete' {
  interface UseAutocompleteProps<
    Value,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
  > {
    checkboxes?: boolean
  }
}

import {
  alpha,
  Checkbox,
  createTheme,
  type PaletteOptions,
  type Theme,
} from '@mui/material'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'
import {
  createCheckedIcon,
  createIndeterminateIcon,
} from './overrides/CheckboxCheckedIcon'

export const deleteButtonStyling = {
  backgroundColor: '#BF3E06',
  color: '#fff',
  border: '1px solid #BF3E06',
  '&:hover': {
    backgroundColor: '#BF3E06',
    boxShadow: '0px 8px 20px #D64709',
  },
  '&:disabled': {
    backgroundColor: '#BF3E06',
    color: '#fff',
  },
}

type AppPalette = PaletteOptions & {
  primary: { main: string; dark: string; contrastText: string }
  secondary: { main: string; contrastText: string }
  error: { main: string }
  info: { main: string }
  text: { primary: string; secondary: string }
}

export function createBaseTheme(palette: AppPalette) {
  const { primary, secondary, error, info, text } = palette

  return createTheme({
    mixins: {
      MuiDataGrid: {
        containerBackground: '#ffffff',
        pinnedBackground: alpha(info.main, 0.0),
      },
    },
    shape: { borderRadius: 2 },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1480,
        xl: 1536,
      },
    },
    palette: {
      ...palette,
      background: { default: '#f6f6fa' },
      divider: '#E6E9F0',
      action: { hover: '#ECEFF3', ...(palette.action ?? {}) },
    },
    typography: {
      fontFamily: ['Noto Sans', 'sans-serif'].join(','),
      h1: {
        fontSize: '48px',
        lineHeight: '58px',
        letterSpacing: 0,
        fontWeight: '600',
        color: text.primary,
      },
      h2: {
        fontSize: '24px',
        lineHeight: '34px',
        letterSpacing: 0,
        fontWeight: '700',
        color: text.primary,
      },
      h3: {
        fontSize: '18px',
        lineHeight: '28px',
        letterSpacing: 0,
        fontWeight: '700',
        color: text.primary,
      },
      h4: {
        fontSize: '16px',
        lineHeight: '28px',
        letterSpacing: 0,
        fontWeight: '600',
        color: text.primary,
        textTransform: 'uppercase',
      },
      h5: {
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: 0,
        fontWeight: '600',
        color: text.primary,
        textTransform: 'uppercase',
      },
      h6: {
        fontSize: '14px',
        lineHeight: '18px',
        letterSpacing: 0,
        fontWeight: '600',
        color: text.primary,
      },
      body1: {
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: 0,
        fontWeight: '400',
        color: text.primary,
      },
      body2: {
        fontSize: '14px',
        lineHeight: '18px',
        letterSpacing: 0,
        fontWeight: '400',
        color: text.primary,
      },
      caption: {
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: 0,
        fontWeight: '400',
        color: text.primary,
      },
    },
    components: {
      MuiPaper: {
        defaultProps: {
          sx: { borderRadius: 0 },
        },
      },
      MuiChip: {
        defaultProps: {
          color: 'primary',
        },
      },
      MuiTooltip: {
        defaultProps: {
          sx: (theme) => ({ color: theme.palette.info.main }),
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          sx: (theme) => ({
            borderRadius: theme.shape.borderRadius,
            textTransform: 'inherit',
            fontWeight: '600',
            lineHeight: '18px',
          }),
        },
        styleOverrides: {
          containedPrimary: {
            color: primary.contrastText,
            border: `1px solid ${primary.main}`,
            '.MuiTouchRipple-root:focus': { color: '#fff' },
            '&:hover': {
              backgroundColor: primary.main,
              boxShadow: `0 4px 10px ${primary.dark}`,
            },
            '&.Mui-disabled': {
              backgroundColor: primary.main,
              opacity: 0.3,
            },
          },
          containedSecondary: {
            color: '#fff',
            '&:hover': { boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)' },
            '&.Mui-disabled': {
              backgroundColor: secondary.main,
              color: '#fff',
              opacity: 0.3,
            },
          },
          outlinedSecondary: {
            '&:hover': { backgroundColor: 'inherit' },
          },
          textPrimary: {
            color: info.main,
            '&:hover': {
              border: 'none',
              backgroundColor: 'inherit',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
            },
            '&.Mui-disabled': {
              color: info.main,
              opacity: 0.3,
            },
          },
          sizeSmall: { fontSize: '12px', height: '30px' },
          sizeMedium: { fontSize: '14px', height: '40px' },
          sizeLarge: { fontSize: '16px', height: '46px' },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          sx: {
            color: secondary.main,
            '&.Mui-focused': { color: secondary.main },
            '&.Mui-error': { color: error.main },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          checkedIcon: createCheckedIcon(primary.contrastText),
          indeterminateIcon: createIndeterminateIcon(primary.contrastText),
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          renderOption: (props, option, state, ownerState) => {
            const { key, ...optionProps } = props
            const label = ownerState.getOptionLabel?.(option) ?? String(option)
            if (
              ownerState.multiple ||
              (ownerState as { checkboxes?: boolean }).checkboxes
            ) {
              return React.createElement(
                'li',
                { key, ...optionProps },
                React.createElement(Checkbox, {
                  checked: state.selected,
                  tabIndex: -1,
                  disableRipple: true,
                  sx: { mr: 1, p: 0.5 },
                }),
                label,
              )
            }
            return React.createElement('li', { key, ...optionProps }, label)
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          sx: (theme) => ({
            color: secondary.main,
            borderRadius: theme.shape.borderRadius,
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            fontSize: '12px',
            lineHeight: '14px',
            alignItems: 'center',
          }),
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          sx: {
            fontSize: '18px',
            color: secondary.main,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          sx: { backgroundColor: '#DDE3EA' },
        },
        styleOverrides: {
          indicator: { bottom: '0' },
        },
      },
      MuiTab: {
        defaultProps: {
          sx: (theme) => ({
            paddingRight: '30px',
            paddingLeft: '30px',
            color: '#000',
            '&.Mui-selected': {
              color: '#000',
              backgroundColor: theme.palette.background.paper,
            },
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }),
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            border: 'none',
            '& .MuiIconButton-root': { color: info.main },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: theme.palette.text.primary,
            },
            // Pinned cells — override MUI's primary-based blends with info-based colors.
            // Must include .MuiDataGrid-virtualScrollerContent to match MUI's generated specificity.
            '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: alpha(
                  info.main,
                  theme.palette.action.hoverOpacity,
                ),
              },
            '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: alpha(
                  info.main,
                  theme.palette.action.selectedOpacity,
                ),
              },
            '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected.Mui-hovered .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected.Mui-hovered .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: alpha(
                  info.main,
                  theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
              },
          }),
          row: ({ theme }: { theme: Theme }) => ({
            '&:hover': {
              backgroundColor: alpha(
                theme.palette.info.main,
                theme.palette.action.hoverOpacity,
              ),
            },
            '&.Mui-selected': {
              backgroundColor: alpha(
                theme.palette.info.main,
                theme.palette.action.selectedOpacity,
              ),
              '&:hover': {
                backgroundColor: alpha(
                  theme.palette.info.main,
                  theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
              },
            },
            '&.Mui-active': {
              backgroundColor: alpha(
                theme.palette.info.main,
                theme.palette.action.activatedOpacity,
              ),
            },
          }),
          columnHeaderTitleContainer: { padding: '0' },
          columnHeaderTitle: { fontWeight: '600' },
          columnHeaders: ({ theme }: { theme: Theme }) => ({
            borderBottom: `2px solid ${theme.palette.divider}`,
          }),
          columnHeader: { '&:focus-within': { outline: 'none' } },
          cell: { '&:focus-within': { outline: 'none' } },
        },
      },
      MuiPaginationItem: {
        defaultProps: {
          sx: (theme) => ({
            '&.Mui-selected': {
              backgroundColor: theme.palette.background.paper,
              color: text.secondary,
            },
          }),
        },
        styleOverrides: {
          text: { color: info.main },
          ellipsis: { color: secondary.main },
          icon: { color: secondary.main },
        },
      },
      MuiFormHelperText: {
        defaultProps: {
          sx: { color: error.main },
        },
      },
      MuiFormControlLabel: {
        defaultProps: {
          sx: {
            color: '#2C2F3D',
            '&.Mui-focused': { color: '#2C2F3D' },
          },
        },
      },
      MuiFormLabel: {
        defaultProps: {
          sx: {
            color: '#2C2F3D',
            '&.Mui-focused': { color: '#2C2F3D' },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          sx: { color: info.main },
        },
      },
    },
  })
}
